import React, { useState, useEffect, ReactNode, useRef } from 'react';
import { ConfigProvider } from 'antd';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '../../theme/ThemeContext';
import FollowUpQuestions, { FollowUpQuestion } from '../FollowUpQuestions/FollowUpQuestions';
import AnalysisSteps, { AnalysisStep } from '../AnalysisSteps/AnalysisSteps';
import ClarificationPrompt, { ClarificationOption } from '../ClarificationPrompt/ClarificationPrompt';
import ResponseFeedback from '../ResponseFeedback/ResponseFeedback';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import Button from '../../atoms/Button/Button';
import Chevron from '../../atoms/Icon/Chevron';
import StopCircle from '../../atoms/Icon/StopCircle';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import HighlightBadge from '../../atoms/HighlightBadge/HighlightBadge';
import useNotification from '../../atoms/Notification/useNotification';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
    Document,
    Packer,
    Paragraph,
    ImageRun,
    HeadingLevel,
    AlignmentType,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import * as XLSX from '@e965/xlsx';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

export interface ChatClarification {
    question: string;
    options: ClarificationOption[];
    multiSelect?: boolean;
}

export interface LoadingCheckpoint {
    timeMs: number;
    message: string;
}

export interface ChatResponseProps {
    id?: string;
    title?: string;
    query?: string;
    thoughtTime?: number | string;
    showAssistantSteps?: boolean;
    responseContent?: ReactNode;
    chartContent?: ReactNode;
    tableContent?: ReactNode;
    followUpQuestions?: FollowUpQuestion[];
    analysisSteps?: AnalysisStep[];
    clarification?: ChatClarification;
    showScrollToTop?: boolean;
    selectionMode?: boolean;
    selected?: boolean;
    isLoading?: boolean;
    loadingCheckpoints?: LoadingCheckpoint[];
    onSelectionChange?: (id: string, selected: boolean) => void;
    onFollowUpClick?: (question: FollowUpQuestion) => void;
    onScrollToTop?: () => void;
    onStopGenerating?: () => void;
    onClarificationSubmit?: (selectedIds: string[]) => void;
    onExport?: (format: 'pdf' | 'ppt' | 'docx') => void;
    onThumbsUp?: () => void;
    onThumbsDown?: () => void;
    onFeedbackSubmit?: (feedback: string) => void;
    onReviewSubmit?: (params: { thumbs_up: boolean; subjective_answer?: string }) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

const DEFAULT_LOADING_CHECKPOINTS: LoadingCheckpoint[] = [
    { timeMs: 5000, message: 'Thinking a bit longer for a better answer…' },
    // { timeMs: 10000, message: 'This analysis is taking longer than expected...' },
];

// Helper function to extract text content from React children
const extractTextContent = (children: any): string => {
    if (typeof children === 'string') {
        return children;
    }
    if (typeof children === 'number') {
        return String(children);
    }
    if (Array.isArray(children)) {
        return children.map(extractTextContent).join('');
    }
    if (children && typeof children === 'object' && 'props' in children) {
        return extractTextContent(children.props?.children);
    }
    return '';
};

// Helper function to convert markdown to Word paragraphs and tables with formatting
const markdownToWordElements = (markdown: string): Array<Paragraph | Table> => {
    const elements: Array<Paragraph | Table> = [];
    const lines = markdown.split('\n');
    let currentParagraph: TextRun[] = [];
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];
    let tableRows: string[] = [];
    let inTable = false;

    const flushParagraph = () => {
        if (currentParagraph.length > 0) {
            elements.push(
                new Paragraph({
                    children: currentParagraph,
                    spacing: { after: 200 },
                }),
            );
            currentParagraph = [];
        }
    };

    const flushCodeBlock = () => {
        if (codeBlockContent.length > 0) {
            elements.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: codeBlockContent.join('\n'),
                            font: 'Courier New',
                            size: 20, // 10pt
                        }),
                    ],
                    spacing: { after: 200 },
                }),
            );
            codeBlockContent = [];
        }
    };

    const flushTable = () => {
        if (tableRows.length > 0) {
            const table = parseMarkdownTable(tableRows);
            if (table) {
                elements.push(table);
            }
            tableRows = [];
            inTable = false;
        }
    };

    const parseMarkdownTable = (rows: string[]): Table | null => {
        if (rows.length < 2) return null; // Need at least header and separator

        // Parse header row
        const headerRow = rows[0]
            .split('|')
            .map((cell) => cell.trim())
            .filter((cell) => cell);
        if (headerRow.length === 0) return null;

        // Skip separator row (second row with dashes)
        const dataRows = rows.slice(2);

        // Create table rows
        const tableRows: TableRow[] = [];

        // Header row
        const headerCells = headerRow.map(
            (header) =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: parseInlineFormatting(header),
                            spacing: { after: 0 },
                        }),
                    ],
                    shading: {
                        fill: 'E6E6E6',
                    },
                }),
        );
        tableRows.push(new TableRow({ children: headerCells }));

        // Data rows
        dataRows.forEach((row) => {
            const cells = row
                .split('|')
                .map((cell) => cell.trim())
                .filter((cell) => cell);
            if (cells.length === headerRow.length) {
                const tableCells = cells.map(
                    (cell) =>
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: parseInlineFormatting(cell),
                                    spacing: { after: 0 },
                                }),
                            ],
                        }),
                );
                tableRows.push(new TableRow({ children: tableCells }));
            }
        });

        return new Table({
            rows: tableRows,
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
        });
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();

        // Handle code blocks
        if (trimmedLine.startsWith('```')) {
            if (inCodeBlock) {
                flushCodeBlock();
                inCodeBlock = false;
            } else {
                flushParagraph();
                flushTable();
                inCodeBlock = true;
            }
            continue;
        }

        if (inCodeBlock) {
            codeBlockContent.push(line);
            continue;
        }

        // Handle markdown tables (detect table rows with | separator)
        const isTableRow = trimmedLine.includes('|') && trimmedLine.split('|').length > 2;
        const isTableSeparator = trimmedLine.match(/^\|[\s\-:]+\|/); // Row with dashes/colons

        if (isTableRow || isTableSeparator) {
            if (!inTable) {
                flushParagraph();
                inTable = true;
            }
            tableRows.push(trimmedLine);
            // Check if next line is not a table row to flush the table
            if (i < lines.length - 1) {
                const nextLine = lines[i + 1].trim();
                const nextIsTableRow = nextLine.includes('|') && nextLine.split('|').length > 2;
                const nextIsTableSeparator = nextLine.match(/^\|[\s\-:]+\|/);
                if (!nextIsTableRow && !nextIsTableSeparator && nextLine !== '') {
                    flushTable();
                }
            }
            continue;
        } else if (inTable) {
            flushTable();
        }

        // Handle headings
        const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            flushParagraph();
            flushTable();
            const level = headingMatch[1].length;
            const text = headingMatch[2];
            const headingLevel =
                level === 1
                    ? HeadingLevel.HEADING_1
                    : level === 2
                      ? HeadingLevel.HEADING_2
                      : level === 3
                        ? HeadingLevel.HEADING_3
                        : level === 4
                          ? HeadingLevel.HEADING_4
                          : level === 5
                            ? HeadingLevel.HEADING_5
                            : HeadingLevel.HEADING_6;

            elements.push(
                new Paragraph({
                    text: text,
                    heading: headingLevel,
                    spacing: { after: 200 },
                }),
            );
            continue;
        }

        // Handle list items
        const listMatch = trimmedLine.match(/^[-*+]\s+(.+)$/);
        if (listMatch) {
            flushParagraph();
            flushTable();
            const text = listMatch[1];
            elements.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: '• ',
                        }),
                        ...parseInlineFormatting(text),
                    ],
                    spacing: { after: 100 },
                    indent: { left: 400 },
                }),
            );
            continue;
        }

        // Handle numbered list items
        const numberedListMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
        if (numberedListMatch) {
            flushParagraph();
            flushTable();
            const text = numberedListMatch[1];
            elements.push(
                new Paragraph({
                    children: parseInlineFormatting(text),
                    spacing: { after: 100 },
                    indent: { left: 400 },
                    numbering: {
                        reference: 'default-numbering',
                        level: 0,
                    },
                }),
            );
            continue;
        }

        // Handle empty lines (paragraph breaks)
        if (trimmedLine === '') {
            flushParagraph();
            flushTable();
            continue;
        }

        // Handle regular text with inline formatting
        const runs = parseInlineFormatting(trimmedLine);
        if (runs.length > 0) {
            currentParagraph.push(...runs);
            // Add space between lines in the same paragraph (if next line is not empty and not a special element)
            if (
                i < lines.length - 1 &&
                lines[i + 1].trim() !== '' &&
                !lines[i + 1].trim().match(/^(#{1,6}|[-*+]|\d+\.|```)/) &&
                !lines[i + 1].trim().includes('|')
            ) {
                currentParagraph.push(new TextRun({ text: ' ' }));
            }
        }
    }

    flushParagraph();
    flushCodeBlock();
    flushTable();

    return elements;
};

// Helper function to parse inline markdown formatting (bold, italic, code, badges)
const parseInlineFormatting = (text: string): TextRun[] => {
    const runs: TextRun[] = [];
    let currentIndex = 0;

    // First, replace badge placeholders with just the value text
    // Handle badge:positive:value or badge:negative:value patterns
    text = text.replace(/badge:(positive|negative):(.+?)(?=\s|$|`|\*|#|\[|\(|\)|,|\.|;|:|<)/g, (match, type, value) => {
        return value; // Replace with just the value
    });

    // Handle HTML span tags that might contain badge content (extract text content)
    // Remove span tags but keep their text content
    text = text.replace(/<span[^>]*>(.*?)<\/span>/gi, (match, content) => {
        // Extract text from HTML content, handling nested tags
        const textContent = content.replace(/<[^>]+>/g, '').trim();
        return textContent || '';
    });

    // Regex patterns for different formatting
    const patterns = [
        { regex: /\*\*\*(.+?)\*\*\*/g, bold: true, italics: true }, // Bold + Italic
        { regex: /\*\*(.+?)\*\*/g, bold: true, italics: false }, // Bold
        { regex: /\*(.+?)\*/g, bold: false, italics: true }, // Italic
        { regex: /`(.+?)`/g, bold: false, italics: false, code: true }, // Inline code
    ];

    // Find all matches with their positions
    const matches: Array<{
        start: number;
        end: number;
        text: string;
        bold: boolean;
        italics: boolean;
        code: boolean;
    }> = [];

    patterns.forEach((pattern) => {
        const regex = new RegExp(pattern.regex.source, 'g');
        let match;
        while ((match = regex.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                text: match[1],
                bold: pattern.bold || false,
                italics: pattern.italics || false,
                code: pattern.code || false,
            });
        }
    });

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep the first one)
    const filteredMatches: typeof matches = [];
    for (let i = 0; i < matches.length; i++) {
        const match = matches[i];
        const overlaps = filteredMatches.some((m) => !(match.end <= m.start || match.start >= m.end));
        if (!overlaps) {
            filteredMatches.push(match);
        }
    }

    // Build text runs
    filteredMatches.forEach((match) => {
        // Add text before the match
        if (match.start > currentIndex) {
            const beforeText = text.substring(currentIndex, match.start);
            if (beforeText) {
                runs.push(new TextRun({ text: beforeText }));
            }
        }

        // Add the formatted text
        runs.push(
            new TextRun({
                text: match.text,
                bold: match.bold,
                italics: match.italics,
                font: match.code ? 'Courier New' : undefined,
                size: match.code ? 20 : undefined, // 10pt for code
            }),
        );

        currentIndex = match.end;
    });

    // Add remaining text
    if (currentIndex < text.length) {
        const remainingText = text.substring(currentIndex);
        if (remainingText) {
            runs.push(new TextRun({ text: remainingText }));
        }
    }

    // If no matches, return the whole text as a single run
    if (runs.length === 0) {
        runs.push(new TextRun({ text: text }));
    }

    return runs;
};

const ChatResponse: React.FC<ChatResponseProps> = (props) => {
    const {
        id,
        title,
        query,
        showAssistantSteps = true,
        responseContent,
        chartContent,
        tableContent,
        followUpQuestions = [],
        analysisSteps = [],
        clarification,
        selectionMode = false,
        selected = false,
        isLoading = false,
        loadingCheckpoints = DEFAULT_LOADING_CHECKPOINTS,
        onSelectionChange,
        onFollowUpClick,
        onStopGenerating,
        onClarificationSubmit,
        onExport,
        onThumbsUp,
        onThumbsDown,
        onFeedbackSubmit,
        onReviewSubmit,
    } = props;

    const theme = useTheme();
    const notificationHook = useNotification();
    const [assistantStepsExpanded, setAssistantStepsExpanded] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

    // Refs for PDF export
    const responseContainerRef = useRef<HTMLDivElement>(null);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    
    // Refs to prevent duplicate notifications and function calls
    const exportInProgressRef = useRef<{ [key: string]: boolean }>({});
    const notificationRef = useRef(notificationHook); // Store notification in ref for stability
    const notificationShownRef = useRef<{ [key: string]: boolean }>({});

    // Keep notification ref updated
    useEffect(() => {
        notificationRef.current = notificationHook;
    }, [notificationHook]);

    // Track elapsed time and update loading message based on checkpoints
    useEffect(() => {
        if (!isLoading) {
            setLoadingMessage(null);
            return;
        }

        // Reset state when loading starts
        setLoadingMessage(null);

        // Sort checkpoints by time to ensure proper order
        const sortedCheckpoints = [...loadingCheckpoints].sort((a, b) => a.timeMs - b.timeMs);

        // Start interval to check elapsed time
        const startTime = Date.now();
        const interval = setInterval(() => {
            const currentElapsed = Date.now() - startTime;

            // Find the most recent checkpoint that has been reached
            let currentCheckpoint: LoadingCheckpoint | null = null;
            for (const checkpoint of sortedCheckpoints) {
                if (currentElapsed >= checkpoint.timeMs) {
                    currentCheckpoint = checkpoint;
                } else {
                    break;
                }
            }

            setLoadingMessage(currentCheckpoint?.message || null);
        }, 100); // Update every 100ms for smooth transitions

        return () => clearInterval(interval);
    }, [isLoading, loadingCheckpoints]);

    const handleSelectionChange = React.useCallback(
        (checked: boolean) => {
            if (onSelectionChange && id) {
                onSelectionChange(id, checked);
            }
        },
        [onSelectionChange, id],
    );

    const handleExport = async (format: 'pdf' | 'ppt' | 'docx') => {
        if (format === 'pdf') {
            await exportToPDF();
        } else if (format === 'docx') {
            await exportToDOCX();
        } else if (onExport) {
            // For other formats, call the parent handler
            onExport(format);
        }
    };

    const exportToPDF = async () => {
        // Prevent duplicate calls
        if (exportInProgressRef.current['pdf']) {
            return;
        }
        exportInProgressRef.current['pdf'] = true;
        
        try {
            // Wait a bit for charts to finish rendering (they render asynchronously)
            await new Promise((resolve) => setTimeout(resolve, 500));

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const maxWidth = pageWidth - 2 * margin;
            let yPosition = margin;
            const lineHeight = 7;
            const spacing = 10;

            // Helper function to add a new page if needed
            const checkPageBreak = (requiredHeight: number) => {
                if (yPosition + requiredHeight > pageHeight - margin) {
                    pdf.addPage();
                    yPosition = margin;
                }
            };

            // Helper function to add text with word wrapping
            const addText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
                pdf.setFontSize(fontSize);
                pdf.setFont('helvetica', isBold ? 'bold' : 'normal');

                const lines = pdf.splitTextToSize(text, maxWidth);
                checkPageBreak(lines.length * lineHeight);

                lines.forEach((line: string) => {
                    if (yPosition + lineHeight > pageHeight - margin) {
                        pdf.addPage();
                        yPosition = margin;
                    }
                    pdf.text(line, margin, yPosition);
                    yPosition += lineHeight;
                });
            };

            // Helper function to capture element as image and add to PDF
            const addImageToPDF = async (element: HTMLElement | null, description: string) => {
                if (!element) return false;

                try {
                    // Wait a bit more for any animations or async rendering
                    await new Promise((resolve) => setTimeout(resolve, 200));

                    const canvas = await html2canvas(element, {
                        backgroundColor: '#ffffff',
                        scale: 2,
                        logging: false,
                        useCORS: true,
                        allowTaint: true,
                    });

                    const imgData = canvas.toDataURL('image/png');
                    const imgWidth = canvas.width / 2; // Scale down for PDF
                    const imgHeight = canvas.height / 2;

                    // Calculate dimensions to fit page width
                    const ratio = maxWidth / imgWidth;
                    const finalWidth = imgWidth * ratio;
                    const finalHeight = imgHeight * ratio;

                    checkPageBreak(finalHeight + spacing);
                    pdf.addImage(imgData, 'PNG', margin, yPosition, finalWidth, finalHeight);
                    yPosition += finalHeight + spacing;
                    return true;
                } catch (error) {
                    console.error(`Error capturing ${description}:`, error);
                    return false;
                }
            };

            // Helper function to capture element while keeping it hidden (uses onclone to avoid UI flash)
            const captureHiddenElement = async (element: HTMLElement): Promise<HTMLCanvasElement> => {
                return await html2canvas(element, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    allowTaint: true,
                    // Use onclone to modify the cloned DOM without affecting the live page
                    onclone: (clonedDoc, clonedElement) => {
                        // Find the export view in the cloned document and make it visible
                        const clonedExportView = clonedElement.closest('[data-chart-export-view]') || 
                                                  clonedElement.closest('[data-table-export-view]') ||
                                                  clonedElement;
                        if (clonedExportView && clonedExportView instanceof HTMLElement) {
                            clonedExportView.style.cssText = 'position: static; width: 800px; background-color: #ffffff;';
                        }
                    },
                });
            };

            // Helper function to capture all charts from carousel export view
            const addAllChartsToPDF = async () => {
                if (!chartContainerRef.current) return false;

                // Look for the export-only container that has all charts rendered
                const exportView = chartContainerRef.current.querySelector('[data-chart-export-view]') as HTMLElement;
                
                if (exportView) {
                    // Get all chart items in the export view
                    const chartItems = exportView.querySelectorAll('[data-chart-export-item]');
                    
                    if (chartItems.length > 0) {
                        // Capture each chart individually for better pagination
                        for (let i = 0; i < chartItems.length; i++) {
                            const chartItem = chartItems[i] as HTMLElement;
                            try {
                                const canvas = await captureHiddenElement(chartItem);
                                const imgData = canvas.toDataURL('image/png');
                                const imgWidth = canvas.width / 2;
                                const imgHeight = canvas.height / 2;
                                const ratio = maxWidth / imgWidth;
                                const finalWidth = imgWidth * ratio;
                                const finalHeight = imgHeight * ratio;
                                checkPageBreak(finalHeight + spacing);
                                pdf.addImage(imgData, 'PNG', margin, yPosition, finalWidth, finalHeight);
                                yPosition += finalHeight + spacing;
                            } catch (error) {
                                console.error(`Error capturing chart ${i + 1}:`, error);
                            }
                        }
                    } else {
                        // Fallback: capture the entire export view
                        try {
                            const canvas = await captureHiddenElement(exportView);
                            const imgData = canvas.toDataURL('image/png');
                            const imgWidth = canvas.width / 2;
                            const imgHeight = canvas.height / 2;
                            const ratio = maxWidth / imgWidth;
                            const finalWidth = imgWidth * ratio;
                            const finalHeight = imgHeight * ratio;
                            checkPageBreak(finalHeight + spacing);
                            pdf.addImage(imgData, 'PNG', margin, yPosition, finalWidth, finalHeight);
                            yPosition += finalHeight + spacing;
                        } catch (error) {
                            console.error('Error capturing all charts:', error);
                        }
                    }
                    
                    return true;
                }
                
                // Fallback: no export view found, capture the visible carousel
                return await addImageToPDF(chartContainerRef.current, 'chart content');
            };

            // Helper function to capture all tables from export view
            const addAllTablesToPDF = async () => {
                if (!tableContainerRef.current) return false;

                // Look for the export-only container that has all tables rendered
                const exportView = tableContainerRef.current.querySelector('[data-table-export-view]') as HTMLElement;
                
                if (exportView) {
                    // Get all table items in the export view
                    const tableItems = exportView.querySelectorAll('[data-table-export-item]');
                    
                    if (tableItems.length > 0) {
                        // Capture each table individually for better pagination
                        for (let i = 0; i < tableItems.length; i++) {
                            const tableItem = tableItems[i] as HTMLElement;
                            try {
                                const canvas = await captureHiddenElement(tableItem);
                                const imgData = canvas.toDataURL('image/png');
                                const imgWidth = canvas.width / 2;
                                const imgHeight = canvas.height / 2;
                                const ratio = maxWidth / imgWidth;
                                const finalWidth = imgWidth * ratio;
                                const finalHeight = imgHeight * ratio;
                                checkPageBreak(finalHeight + spacing);
                                pdf.addImage(imgData, 'PNG', margin, yPosition, finalWidth, finalHeight);
                                yPosition += finalHeight + spacing;
                            } catch (error) {
                                console.error(`Error capturing table ${i + 1}:`, error);
                            }
                        }
                    } else {
                        // Fallback: capture the entire export view
                        try {
                            const canvas = await captureHiddenElement(exportView);
                            const imgData = canvas.toDataURL('image/png');
                            const imgWidth = canvas.width / 2;
                            const imgHeight = canvas.height / 2;
                            const ratio = maxWidth / imgWidth;
                            const finalWidth = imgWidth * ratio;
                            const finalHeight = imgHeight + spacing;
                            checkPageBreak(finalHeight + spacing);
                            pdf.addImage(imgData, 'PNG', margin, yPosition, finalWidth, finalHeight);
                            yPosition += finalHeight + spacing;
                        } catch (error) {
                            console.error('Error capturing all tables:', error);
                        }
                    }
                    
                    return true;
                }
                
                // Fallback: no export view found, capture the visible table
                return await addImageToPDF(tableContainerRef.current, 'table content');
            };

            // Add title if present
            if (title) {
                addText(title, 16, true);
                yPosition += spacing;
            }

            // Add query if present
            if (query) {
                addText(`Query: ${query}`, 12, true);
                yPosition += spacing;
            }

            // Add response text content
            if (responseContent) {
                if (responseContainerRef.current) {
                    const success = await addImageToPDF(responseContainerRef.current, 'response content');
                    if (!success) {
                        // Fallback to text extraction
                        const textContent =
                            typeof responseContent === 'string' ? responseContent : extractTextContent(responseContent);
                        if (textContent) {
                            addText(textContent, 12);
                            yPosition += spacing;
                        }
                    }
                } else if (typeof responseContent === 'string') {
                    // Fallback: if no ref, try to extract text
                    addText(responseContent, 12);
                    yPosition += spacing;
                }
            }

            // Add all charts (including those behind the carousel)
            if (chartContent && chartContainerRef.current) {
                await addAllChartsToPDF();
            }

            // Add all tables (including those behind any carousel)
            if (tableContent && tableContainerRef.current) {
                await addAllTablesToPDF();
            }

            // Generate filename
            const filename = title
                ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.pdf`
                : `chat_response_${Date.now()}.pdf`;

            // Save the PDF
            pdf.save(filename);
            
            // Show success notification (only once per export)
            if (!notificationShownRef.current['pdf']) {
                notificationShownRef.current['pdf'] = true;
                notificationRef.current.success({
                    message: 'Export completed',
                    description: 'PDF has been downloaded successfully.',
                    duration: 3,
                });
                // Reset notification flag after notification duration
                setTimeout(() => {
                    notificationShownRef.current['pdf'] = false;
                }, 3000);
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            // Fallback: call parent handler if provided
            if (onExport) {
                onExport('pdf');
            }
        } finally {
            // Reset the flag after a delay to allow for re-exports
            setTimeout(() => {
                exportInProgressRef.current['pdf'] = false;
            }, 1000);
        }
    };

    const exportChartToPNG = async () => {
        if (!chartContainerRef.current) return;
        
        // Prevent duplicate calls
        if (exportInProgressRef.current['chart']) {
            return;
        }
        exportInProgressRef.current['chart'] = true;

        try {
            // Wait a bit for chart to finish rendering
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Look for the export-only container that has all charts rendered
            const exportView = chartContainerRef.current.querySelector('[data-chart-export-view]') as HTMLElement;
            
            const targetElement: HTMLElement = exportView || chartContainerRef.current;

            // Use onclone to modify the cloned DOM without affecting the live page
            const canvas = await html2canvas(targetElement, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true,
                allowTaint: true,
                onclone: (clonedDoc, clonedElement) => {
                    // Make the cloned export view visible for capture
                    if (clonedElement instanceof HTMLElement) {
                        clonedElement.style.cssText = 'position: static; width: 800px; background-color: #ffffff;';
                    }
                },
            });

            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const filename = title
                        ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_chart_${Date.now()}.png`
                        : `chart_${Date.now()}.png`;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    
                    // Show success notification (only once per export)
                    if (!notificationShownRef.current['chart']) {
                        notificationShownRef.current['chart'] = true;
                        notificationRef.current.success({
                            message: 'Download completed',
                            description: 'Chart has been downloaded successfully.',
                            duration: 3,
                        });
                        // Reset notification flag after notification duration
                        setTimeout(() => {
                            notificationShownRef.current['chart'] = false;
                        }, 3000);
                    }
                }
            }, 'image/png');
        } catch (error) {
            console.error('Error exporting chart to PNG:', error);
        } finally {
            // Reset the flag after a delay to allow for re-exports
            setTimeout(() => {
                exportInProgressRef.current['chart'] = false;
            }, 1000);
        }
    };

    const exportTableToExcel = async () => {
        if (!tableContainerRef.current) {
            console.error('Table container ref is not available');
            return;
        }
        
        // Prevent duplicate calls
        if (exportInProgressRef.current['table']) {
            return;
        }
        exportInProgressRef.current['table'] = true;

        try {
            // Wait a bit for table to finish rendering
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Try multiple selectors to find the table
            const table =
                tableContainerRef.current
                    .querySelector('.ant-table-tbody')
                    ?.closest('.ant-table')
                    ?.querySelector('table') ||
                tableContainerRef.current.querySelector('.ant-table table') ||
                tableContainerRef.current.querySelector('table.ant-table') ||
                tableContainerRef.current.querySelector('table');

            if (!table) {
                console.error('Table element not found in container');
                return;
            }

            // Get headers - try multiple approaches
            const headers: string[] = [];

            // Try finding headers in thead
            const thead = table.querySelector('thead');
            if (thead) {
                const headerRow = thead.querySelector('tr');
                if (headerRow) {
                    const headerCells = headerRow.querySelectorAll('th, .ant-table-cell');
                    headerCells.forEach((cell) => {
                        const text = cell.textContent?.trim() || cell.getAttribute('title') || '';
                        if (text) headers.push(text);
                    });
                }
            }

            // If no headers found, try .ant-table-thead
            if (headers.length === 0) {
                const antThead = tableContainerRef.current.querySelector('.ant-table-thead');
                if (antThead) {
                    const headerRow = antThead.querySelector('tr');
                    if (headerRow) {
                        const headerCells = headerRow.querySelectorAll('th, .ant-table-cell');
                        headerCells.forEach((cell) => {
                            const text = cell.textContent?.trim() || cell.getAttribute('title') || '';
                            if (text) headers.push(text);
                        });
                    }
                }
            }

            if (headers.length === 0) {
                console.error('No headers found in table');
                return;
            }

            // Get rows - try multiple approaches
            const rows: any[][] = [];

            // Try finding rows in tbody
            const tbody = table.querySelector('tbody');
            if (tbody) {
                const bodyRows = tbody.querySelectorAll('tr');
                bodyRows.forEach((row) => {
                    const rowData: any[] = [];
                    const cells = row.querySelectorAll('td, .ant-table-cell');
                    cells.forEach((cell) => {
                        const text = cell.textContent?.trim() || '';
                        rowData.push(text);
                    });
                    // Ensure row has same number of columns as headers
                    while (rowData.length < headers.length) {
                        rowData.push('');
                    }
                    rows.push(rowData);
                });
            }

            // If no rows found, try .ant-table-tbody
            if (rows.length === 0) {
                const antTbody = tableContainerRef.current.querySelector('.ant-table-tbody');
                if (antTbody) {
                    const bodyRows = antTbody.querySelectorAll('tr');
                    bodyRows.forEach((row) => {
                        const rowData: any[] = [];
                        const cells = row.querySelectorAll('td, .ant-table-cell');
                        cells.forEach((cell) => {
                            const text = cell.textContent?.trim() || '';
                            rowData.push(text);
                        });
                        while (rowData.length < headers.length) {
                            rowData.push('');
                        }
                        rows.push(rowData);
                    });
                }
            }

            if (rows.length === 0) {
                console.error('No data rows found in table');
                return;
            }

            // Create workbook and worksheet
            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

            // Generate filename
            const filename = title
                ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_table_${Date.now()}.xlsx`
                : `table_${Date.now()}.xlsx`;

            // Save file
            XLSX.writeFile(workbook, filename);
            
            // Show success notification (only once per export)
            if (!notificationShownRef.current['table']) {
                notificationShownRef.current['table'] = true;
                notificationRef.current.success({
                    message: 'Download completed',
                    description: 'Table has been downloaded successfully.',
                    duration: 3,
                });
                // Reset notification flag after notification duration
                setTimeout(() => {
                    notificationShownRef.current['table'] = false;
                }, 3000);
            }
        } catch (error) {
            console.error('Error exporting table to Excel:', error);
            console.error('Error details:', error instanceof Error ? error.stack : error);
        } finally {
            // Reset the flag after a delay to allow for re-exports
            setTimeout(() => {
                exportInProgressRef.current['table'] = false;
            }, 1000);
        }
    };

    const exportToDOCX = async () => {
        // Prevent duplicate calls
        if (exportInProgressRef.current['docx']) {
            return;
        }
        exportInProgressRef.current['docx'] = true;
        
        try {
            // Wait a bit for charts to finish rendering (they render asynchronously)
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Build DOCX document sections (can contain both Paragraph and Table)
            const docSections: Array<Paragraph | Table> = [];

            // Add title
            if (title) {
                docSections.push(
                    new Paragraph({
                        text: title,
                        heading: HeadingLevel.HEADING_1,
                        spacing: { after: 200 },
                    }),
                );
            }

            // Add query if present
            if (query) {
                docSections.push(
                    new Paragraph({
                        text: `Query: ${query}`,
                        heading: HeadingLevel.HEADING_2,
                        spacing: { after: 200 },
                    }),
                );
            }

            // Add markdown content with formatting (converts markdown to Word formatting)
            if (responseContent) {
                // If responseContent is a string, convert markdown to formatted Word elements
                if (typeof responseContent === 'string') {
                    const formattedElements = markdownToWordElements(responseContent);
                    // Add all elements (both paragraphs and tables) to docSections
                    formattedElements.forEach((element) => {
                        docSections.push(element);
                    });
                } else {
                    // If responseContent is not a string (ReactNode), extract text content
                    const textContent = extractTextContent(responseContent);
                    if (textContent.trim()) {
                        const formattedElements = markdownToWordElements(textContent);
                        formattedElements.forEach((element) => {
                            docSections.push(element);
                        });
                    }
                }
            }

            // Helper function to capture hidden element for DOCX (uses onclone to avoid UI flash)
            const captureHiddenElementForDocx = async (element: HTMLElement): Promise<string | null> => {
                try {
                    await new Promise((resolve) => setTimeout(resolve, 200));
                    
                    const canvas = await html2canvas(element, {
                        backgroundColor: '#ffffff',
                        scale: 2,
                        logging: false,
                        useCORS: true,
                        allowTaint: true,
                        onclone: (clonedDoc, clonedElement) => {
                            // Make the cloned export view visible for capture
                            const clonedExportView = clonedElement.closest('[data-chart-export-view]') || 
                                                      clonedElement.closest('[data-table-export-view]') ||
                                                      clonedElement;
                            if (clonedExportView && clonedExportView instanceof HTMLElement) {
                                clonedExportView.style.cssText = 'position: static; width: 800px; background-color: #ffffff;';
                            }
                        },
                    });
                    
                    return canvas.toDataURL('image/png');
                } catch (error) {
                    console.error('Error capturing element:', error);
                    return null;
                }
            };

            // Helper to add image paragraph to docSections
            const addImageToDocSections = async (element: HTMLElement) => {
                const imageData = await captureHiddenElementForDocx(element);
                if (imageData) {
                    const base64Match = imageData.match(/^data:image\/[^;]+;base64,(.+)$/);
                    if (base64Match) {
                        const base64Data = base64Match[1];
                        const imageBuffer = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));

                        // Create a temporary image to get dimensions
                        const img = new Image();
                        await new Promise((resolve) => {
                            img.onload = resolve;
                            img.src = imageData;
                        });

                        const width = Math.min(img.width * 0.375, 500); // scale down from 2x
                        const height = (img.height / img.width) * width;

                        docSections.push(
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        data: imageBuffer,
                                        transformation: {
                                            width: width,
                                            height: height,
                                        },
                                        type: 'png',
                                    }),
                                ],
                                spacing: { after: 200 },
                                alignment: AlignmentType.CENTER,
                            }),
                        );
                    }
                }
            };

            // Capture and add chart images (including all charts from carousel)
            if (chartContent && chartContainerRef.current) {
                try {
                    const exportView = chartContainerRef.current.querySelector('[data-chart-export-view]') as HTMLElement;
                    
                    if (exportView) {
                        const chartItems = exportView.querySelectorAll('[data-chart-export-item]');
                        
                        if (chartItems.length > 0) {
                            for (let i = 0; i < chartItems.length; i++) {
                                await addImageToDocSections(chartItems[i] as HTMLElement);
                            }
                        } else {
                            await addImageToDocSections(exportView);
                        }
                    } else {
                        await addImageToDocSections(chartContainerRef.current);
                    }
                } catch (error) {
                    console.error('Error adding chart image to DOCX:', error);
                }
            }

            // Capture and add table images (including all tables from any carousel)
            if (tableContent && tableContainerRef.current) {
                try {
                    const exportView = tableContainerRef.current.querySelector('[data-table-export-view]') as HTMLElement;
                    
                    if (exportView) {
                        const tableItems = exportView.querySelectorAll('[data-table-export-item]');
                        
                        if (tableItems.length > 0) {
                            for (let i = 0; i < tableItems.length; i++) {
                                await addImageToDocSections(tableItems[i] as HTMLElement);
                            }
                        } else {
                            await addImageToDocSections(exportView);
                        }
                    } else {
                        await addImageToDocSections(tableContainerRef.current);
                    }
                } catch (error) {
                    console.error('Error adding table image to DOCX:', error);
                }
            }

            // Create the document with all sections
            const doc = new Document({
                sections: [
                    {
                        children: docSections,
                    },
                ],
            });

            // Generate and save the DOCX file
            const blob = await Packer.toBlob(doc);
            const filename = title
                ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.docx`
                : `chat_response_${Date.now()}.docx`;
            saveAs(blob, filename);
            
            // Show success notification (only once per export)
            if (!notificationShownRef.current['docx']) {
                notificationShownRef.current['docx'] = true;
                notificationRef.current.success({
                    message: 'Export completed',
                    description: 'Document has been downloaded successfully.',
                    duration: 3,
                });
                // Reset notification flag after notification duration
                setTimeout(() => {
                    notificationShownRef.current['docx'] = false;
                }, 3000);
            }
        } catch (error) {
            console.error('Error exporting to DOCX:', error);
            // Fallback: call parent handler if provided
            if (onExport) {
                onExport('docx');
            }
        } finally {
            // Reset the flag after a delay to allow for re-exports
            setTimeout(() => {
                exportInProgressRef.current['docx'] = false;
            }, 1000);
        }
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="w-full max-w-[1112px] mx-auto"
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-chat-response
            >
                {/* Title */}
                {title && (
                    <div className="mb-4 py-4">
                        <h3 className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                    </div>
                )}

                {/* Query Display */}
                {query && (
                    <div className="flex justify-end mb-4 py-2.5 px-4">
                        <div className="bg-[rgba(0,0,0,0.04)] rounded-lg px-3 py-2 max-w-[567px]">
                            <p className="font-['Open_Sans'] font-normal text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] m-0">
                                {query}
                            </p>
                        </div>
                    </div>
                )}

                {/* Stop Generating Button */}
                {isLoading && onStopGenerating && (
                    <div className="flex items-center mb-4">
                        <button
                            onClick={onStopGenerating}
                            className="bg-[rgba(0,0,0,0.02)] rounded-lg px-3 py-2 flex items-center gap-2 border-none cursor-pointer hover:bg-[rgba(0,0,0,0.04)] transition-colors"
                            data-test-id={`${props['data-test-id']}-stop-generating`}
                        >
                            <StopCircle size={14} color="#44338a" />
                            <span className="font-['Open_Sans'] font-normal text-[14px] leading-[normal] text-[#44338a]">
                                Stop generating
                            </span>
                        </button>
                    </div>
                )}

                {/* Thought Time Indicator */}
                {/* {thoughtTime && (
                    <div className="mb-2">
                        <p className="font-['Open_Sans'] font-normal text-[12px] leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            Thought for {thoughtTime}
                        </p>
                    </div>
                )} */}

                {/* Analysis Steps Toggle - Only shown after generation is complete */}
                {!isLoading && showAssistantSteps && analysisSteps.length > 0 && (
                    <>
                        <div
                            className="flex items-center gap-1 mb-4 cursor-pointer"
                            onClick={() => setAssistantStepsExpanded(!assistantStepsExpanded)}
                        >
                            <p className="font-['Open_Sans'] font-normal text-[12px] leading-[20px] text-[rgba(0,0,0,0.65)] m-0">
                                Analysis Steps
                            </p>
                            <Chevron size={16} rotate={assistantStepsExpanded ? 180 : 90} color="rgba(0,0,0,0.65)" />
                        </div>
                        {assistantStepsExpanded && (
                            <div className="mb-4">
                                <AnalysisSteps
                                    steps={analysisSteps}
                                    data-test-id={`${props['data-test-id']}-analysis-steps`}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Analysis Steps During Loading */}
                {isLoading && analysisSteps.length > 0 && !assistantStepsExpanded && (
                    <div className="mb-4">
                        <AnalysisSteps steps={analysisSteps} data-test-id={`${props['data-test-id']}-analysis-steps`} />
                    </div>
                )}

                {/* Clarification Prompt */}
                {clarification && (
                    <div className="mb-4">
                        <ClarificationPrompt
                            question={clarification.question}
                            options={clarification.options}
                            multiSelect={clarification.multiSelect}
                            onSubmit={onClarificationSubmit}
                            data-test-id={`${props['data-test-id']}-clarification`}
                        />
                    </div>
                )}

                {/* Main Response Container */}
                {isLoading && analysisSteps.length === 0 ? (
                    <div className="rounded-lg p-5 mb-4 bg-[#fbfaff]">
                        {/* Loading Indicator with Time-based Messages */}
                        <div className="flex items-center">
                            <p className="font-['Open_Sans'] font-semibold text-[16px] leading-[22px] text-black m-0">
                                {loadingMessage ? (
                                    loadingMessage
                                ) : (
                                    <>
                                        <span className="inline-block animate-pulse">.</span>
                                        <span className="inline-block animate-pulse" style={{ animationDelay: '0.2s' }}>
                                            .
                                        </span>
                                        <span className="inline-block animate-pulse" style={{ animationDelay: '0.4s' }}>
                                            .
                                        </span>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                ) : (
                    !isLoading && (
                        <div className="bg-white rounded-lg p-5 mb-4">
                            <>
                                {/* Text Response Content */}
                                {responseContent && (
                                    <div className="mb-6" ref={responseContainerRef}>
                                        <div className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] flex items-start gap-2">
                                            {/* Selection Checkbox - positioned at the start of response */}
                                            {selectionMode && id && (
                                                <div className="flex-shrink-0 mt-0.5">
                                                    <Checkbox
                                                        checked={selected}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectionChange(e.target.checked);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-test-id={`${props['data-test-id']}-select-checkbox`}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 markdown-content">
                                                {typeof responseContent === 'string' ? (
                                                    <ReactMarkdown
                                                        rehypePlugins={[rehypeRaw]}
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            h1: ({ node, ...props }) => (
                                                                <h1
                                                                    className="font-['Open_Sans'] font-bold text-[20px] leading-[28px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            h2: ({ node, ...props }) => (
                                                                <h2
                                                                    className="font-['Open_Sans'] font-bold text-[18px] leading-[26px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            h3: ({ node, ...props }) => (
                                                                <h3
                                                                    className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            h4: ({ node, ...props }) => (
                                                                <h4
                                                                    className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            h5: ({ node, ...props }) => (
                                                                <h5
                                                                    className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            h6: ({ node, ...props }) => (
                                                                <h6
                                                                    className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            p: ({ node, ...props }) => (
                                                                <p
                                                                    className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 m-0"
                                                                    {...props}
                                                                />
                                                            ),
                                                            ul: ({ node, ...props }) => (
                                                                <ul
                                                                    className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-disc"
                                                                    {...props}
                                                                />
                                                            ),
                                                            ol: ({ node, ...props }) => (
                                                                <ol
                                                                    className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-decimal"
                                                                    {...props}
                                                                />
                                                            ),
                                                            li: ({ node, ...props }) => (
                                                                <li className="mb-1" {...props} />
                                                            ),
                                                            table: ({ node, ...props }) => (
                                                                <div className="overflow-x-auto my-3">
                                                                    <table
                                                                        className="min-w-full border-collapse border border-[rgba(0,0,0,0.06)]"
                                                                        {...props}
                                                                    />
                                                                </div>
                                                            ),
                                                            thead: ({ node, ...props }) => (
                                                                <thead className="bg-[rgba(0,0,0,0.02)]" {...props} />
                                                            ),
                                                            tbody: ({ node, ...props }) => <tbody {...props} />,
                                                            tr: ({ node, ...props }) => (
                                                                <tr
                                                                    className="border-b border-[rgba(0,0,0,0.06)]"
                                                                    {...props}
                                                                />
                                                            ),
                                                            th: ({ node, ...props }) => (
                                                                <th
                                                                    className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 text-left border border-[rgba(0,0,0,0.06)]"
                                                                    {...props}
                                                                />
                                                            ),
                                                            td: ({ node, ...props }) => (
                                                                <td
                                                                    className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 border border-[rgba(0,0,0,0.06)]"
                                                                    {...props}
                                                                />
                                                            ),
                                                            span: ({ node, ...props }: any) => {
                                                                // Check if this span has a color style that indicates it should be a badge
                                                                // The style attribute comes from node.properties when using rehype-raw
                                                                const styleAttr =
                                                                    (node?.properties as any)?.style ||
                                                                    (props as any).style;
                                                                const children = props.children;

                                                                // Extract text content from children
                                                                const textContent = extractTextContent(children);

                                                                // Parse style attribute to check for color:green or color:red
                                                                if (styleAttr && typeof styleAttr === 'string') {
                                                                    const colorMatch =
                                                                        styleAttr.match(/color\s*:\s*(green|red)/i);
                                                                    if (colorMatch) {
                                                                        const color = colorMatch[1].toLowerCase();
                                                                        const badgeType =
                                                                            color === 'green' ? 'positive' : 'negative';
                                                                        return (
                                                                            <HighlightBadge
                                                                                value={textContent.trim()}
                                                                                type={badgeType}
                                                                            />
                                                                        );
                                                                    }
                                                                }

                                                                // Fallback to regular span rendering
                                                                return <span {...props} />;
                                                            },
                                                            code: ({ node, ...props }: any) => {
                                                                const isInline = (props as any).inline;
                                                                const codeContent = String(props.children || '');

                                                                // Check if this is a badge placeholder: badge:positive:value or badge:negative:value
                                                                const badgeMatch = codeContent.match(
                                                                    /^badge:(positive|negative):(.+)$/,
                                                                );
                                                                if (badgeMatch && isInline) {
                                                                    const badgeType = badgeMatch[1] as
                                                                        | 'positive'
                                                                        | 'negative';
                                                                    const badgeValue = badgeMatch[2];
                                                                    return (
                                                                        <HighlightBadge
                                                                            value={badgeValue}
                                                                            type={badgeType}
                                                                        />
                                                                    );
                                                                }

                                                                return isInline ? (
                                                                    <code
                                                                        className="bg-[rgba(0,0,0,0.06)] px-1 py-0.5 rounded text-[13px] font-mono"
                                                                        {...props}
                                                                    />
                                                                ) : (
                                                                    <code
                                                                        className="block bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 text-[13px] font-mono overflow-x-auto"
                                                                        {...props}
                                                                    />
                                                                );
                                                            },
                                                            pre: ({ node, ...props }) => (
                                                                <pre
                                                                    className="bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 overflow-x-auto"
                                                                    {...props}
                                                                />
                                                            ),
                                                            blockquote: ({ node, ...props }) => (
                                                                <blockquote
                                                                    className="border-l-4 border-[rgba(0,0,0,0.2)] pl-4 my-2 italic text-[rgba(0,0,0,0.65)]"
                                                                    {...props}
                                                                />
                                                            ),
                                                            strong: ({ node, ...props }) => (
                                                                <strong className="font-bold" {...props} />
                                                            ),
                                                            em: ({ node, ...props }) => (
                                                                <em className="italic" {...props} />
                                                            ),
                                                        }}
                                                    >
                                                        {responseContent}
                                                    </ReactMarkdown>
                                                ) : (
                                                    responseContent
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Selection Checkbox for non-text content (charts/tables) */}
                                {!responseContent && selectionMode && id && (
                                    <div className="flex justify-end mb-2">
                                        <Checkbox
                                            checked={selected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleSelectionChange(e.target.checked);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            data-test-id={`${props['data-test-id']}-select-checkbox`}
                                        />
                                    </div>
                                )}

                                {/* Chart Placeholder */}
                                {chartContent && (
                                    <div className="mb-6 relative" ref={chartContainerRef}>
                                        <div className="absolute top-2 right-2 z-10">
                                            <Button
                                                type="text"
                                                icon={<DownloadFile size={16} />}
                                                onClick={exportChartToPNG}
                                                className="!p-2"
                                                aria-label="Download chart as PNG"
                                                data-test-id={`${props['data-test-id']}-chart-download-btn`}
                                            />
                                        </div>
                                        <div>{chartContent}</div>
                                    </div>
                                )}

                                {/* Table Placeholder */}
                                {tableContent && (
                                    <div className="mb-6 relative" ref={tableContainerRef}>
                                        <div className="absolute top-2 right-2 z-10">
                                            <Button
                                                type="text"
                                                icon={<DownloadFile size={16} />}
                                                onClick={exportTableToExcel}
                                                className="!p-2"
                                                aria-label="Download table as Excel"
                                                data-test-id={`${props['data-test-id']}-table-download-btn`}
                                            />
                                        </div>
                                        <div>{tableContent}</div>
                                    </div>
                                )}

                                {/* Follow-up Questions */}
                                {followUpQuestions.length > 0 && (
                                    <div className="mt-6">
                                        <FollowUpQuestions
                                            questions={followUpQuestions}
                                            onQuestionClick={onFollowUpClick}
                                            data-test-id={`${props['data-test-id']}-follow-up`}
                                        />
                                    </div>
                                )}
                            </>
                        </div>
                    )
                )}

                {/* Response Feedback */}
                {!isLoading && (
                    <div className="mb-4 flex justify-end">
                        <ResponseFeedback
                            onExport={handleExport}
                            onThumbsUp={onThumbsUp}
                            onThumbsDown={onThumbsDown}
                            onFeedbackSubmit={onFeedbackSubmit}
                            onReviewSubmit={onReviewSubmit}
                            data-test-id={`${props['data-test-id']}-feedback`}
                        />
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default ChatResponse;
